import React, { useRef, useState, useEffect } from 'react';
import Handsontable from 'handsontable';
import { HotTable } from '@handsontable/react';
import { HyperFormula } from 'hyperformula';
import 'handsontable/dist/handsontable.full.min.css';
import './formula-highlight.css';
import defaultSheetsRaw from './assets/default-sheets.json';
import Report from './Report';
import { getForecastSummaryCellClassNames, forecastSummaryMerges, forecastSummaryColWidths } from './sheetStyles/ForecastSummaryStyle';
import { getTotalSummaryCellClassNames, totalSummaryMerges, totalSummaryColWidths } from './sheetStyles/TotalSummaryStyle';
import { getSinkingFundCellClassNames, sinkingFundMerges, sinkingFundColWidths } from './sheetStyles/SinkingFundDetailsStyle';
import { getReportCellClassNames, reportMerges, reportColWidths } from './sheetStyles/ReportStyle';

console.log(Handsontable);

const defaultSheets: Record<string, any> = defaultSheetsRaw;

const SHEETS = [
  'Forecast Summary',
  'Total Summary',
  'Sinking Fund Details',
];

// Generate column headers from 'A' to 'AS'
const colCount = 45; // 'A' to 'AS'
const colHeaders = Array.from({ length: colCount }, (_, i) => {
  let col = '';
  let n = i;
  do {
    col = String.fromCharCode(65 + (n % 26)) + col;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return col;
});

// Generate 300 rows: first row is headers, next 299 are data
const initialData = [
  Array(colCount + 1).fill(''),
  ...Array.from({ length: 299 }, (_, i) => {
    const row = [];
    for (let j = 0; j < colCount + 1; j++) {
      row.push('');
    }
    return row;
  }),
];

const sheetDataMap: Record<string, any[][]> = {
  'Forecast Summary': JSON.parse(JSON.stringify(initialData)),
  'Total Summary': JSON.parse(JSON.stringify(initialData)),
  'Sinking Fund Details': JSON.parse(JSON.stringify(initialData)),
};

// Create a single HyperFormula instance with the correct license key
const hfInstance = HyperFormula.buildEmpty({
  licenseKey: 'gpl-v3'
});


// Declare _hf on the Window interface for TypeScript
// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare global {
  interface Window {
    _hf?: any;
  }
}

if (import.meta.env.PROD) window._hf = hfInstance;

// --- Add these maps to store colWidths for each sheet ---
const sheetMergeCellsMap: Record<string, any[]> = {
  'Forecast Summary': forecastSummaryMerges,
  'Total Summary': totalSummaryMerges,
  'Sinking Fund Details': sinkingFundMerges,
  'Report': reportMerges,
};

export default function Calculations() {
  const [sheetDataMapState, setSheetDataMapState] = useState<Record<string, any[][]>>({ ...sheetDataMap });
  const [currentSheet, setCurrentSheet] = useState(SHEETS[0]);
  const [data, setData] = useState(sheetDataMapState[SHEETS[0]]);
  const [mergeCells, setMergeCells] = useState<any[]>(sheetMergeCellsMap[SHEETS[0]]);
  const [formulaBarValue, setFormulaBarValue] = useState('');
  const hotRef = useRef<any>(null);
  const selectedCellRef = useRef<[number, number] | null>(null);
  const formulaBarValueRef = useRef('');
  const [showHidden, setShowHidden] = useState(false);
  const formulaRefsRef = useRef<Set<string>>(new Set());
  const [isEditingFormulaBar, setIsEditingFormulaBar] = useState(false);
  const formulaBarInputRef = useRef<HTMLInputElement>(null);
  const [isEditingCell, setIsEditingCell] = useState(false);
  const cellEditorRef = useRef<any>(null);
  const copySourceRef = useRef<{
    startRow: number;
    startCol: number;
    endRow: number;
    endCol: number;
    data: (string | null)[][];
  } | null>(null);
  const [page, setPage] = useState('Report');
  const [userColWidths, setUserColWidths] = useState<Record<string, number[]>>({});

  // On mount, initialize all sheets in HyperFormula and set their content
  React.useEffect(() => {
    if (defaultSheets) {
      console.log('[HF INIT] Starting initialization of all sheets');
      for (const sheetName of SHEETS) {
        // Add sheet to HyperFormula if it doesn't exist
        if (!hfInstance.doesSheetExist(sheetName)) {
          hfInstance.addSheet(sheetName);
          console.log(`[HF INIT] Added sheet to HyperFormula: ${sheetName}`);
        } else {
          console.log(`[HF INIT] Sheet already exists in HyperFormula: ${sheetName}`);
        }
        // Set sheet content in HyperFormula
        const sheetId = hfInstance.getSheetId(sheetName);
        if (sheetId !== undefined) {
          let sheetData = [];
          if (defaultSheets[sheetName]?.data) {
            sheetData = defaultSheets[sheetName].data;
            console.log(`[HF INIT] Using .data for sheet: ${sheetName}`);
          } else {
            sheetData = defaultSheets[sheetName];
            console.log(`[HF INIT] Using raw array for sheet: ${sheetName}`);
          }
          hfInstance.setSheetContent(sheetId, sheetData);
          console.log(`[HF INIT] Set content for sheet: ${sheetName}, rows: ${sheetData.length}`);
        } else {
          console.warn(`[HF INIT] Could not get sheetId for: ${sheetName}`);
        }
      }
      // Optionally, update state to ensure UI is in sync
      const newSheetDataMapState: Record<string, any[][]> = { ...sheetDataMapState };
      for (const sheetName of SHEETS) {
        if (defaultSheets[sheetName]?.data) {
          newSheetDataMapState[sheetName] = defaultSheets[sheetName].data;
        } else {
          newSheetDataMapState[sheetName] = defaultSheets[sheetName];
        }
      }
      setSheetDataMapState(newSheetDataMapState);
      setData(newSheetDataMapState[currentSheet]);
      console.log('[HF INIT] Finished initialization. Current HyperFormula sheets:', hfInstance.getSheetNames());
      console.log('[HF INIT] Current sheetDataMapState:', newSheetDataMapState);
    } else {
      console.warn('[HF INIT] No defaultSheets found');
    }
    // eslint-disable-next-line
  }, []);

  // Auto-fit columns after data is loaded or sheet changes
  React.useEffect(() => {
    const hot = hotRef.current?.hotInstance;
    if (hot) {
      // Handsontable's autoColumnSize plugin: force re-calculate all columns
      const autoCol = hot.getPlugin('autoColumnSize');
      if (autoCol && autoCol.recalculateAllColumnsWidth) {
        autoCol.recalculateAllColumnsWidth();
        hot.render();
      }
    }
  }, [data, currentSheet]);

  // Ensure hidden rows are set after loading defaultSheets for Sinking Fund Details
  React.useEffect(() => {
    if (
      currentSheet === 'Sinking Fund Details' &&
      !showHidden &&
      sheetDataMapState['Sinking Fund Details'].length > 1
    ) {
      // This will trigger a re-render with the correct hiddenRows
      setData(sheetDataMapState['Sinking Fund Details']);
    }
    // eslint-disable-next-line
  }, [currentSheet, showHidden]);

  // On mount or sheet change, sync mergeCells state from style files
  React.useEffect(() => {
    let initialMerges: any[] = [];
    if (currentSheet === 'Forecast Summary') {
      initialMerges = forecastSummaryMerges;
    } else if (currentSheet === 'Total Summary') {
      initialMerges = totalSummaryMerges;
    } else if (currentSheet === 'Sinking Fund Details') {
      initialMerges = sinkingFundMerges;
    } else if (currentSheet === 'Report') {
      initialMerges = reportMerges;
    }
    setMergeCells(initialMerges);
  }, [currentSheet]);

  // Utility to convert row/col to Excel reference (e.g., A1)
  function toExcelRef(row: number, col: number) {
    let colRef = '';
    let n = col;
    do {
      colRef = String.fromCharCode(65 + (n % 26)) + colRef;
      n = Math.floor(n / 26) - 1;
    } while (n >= 0);
    return colRef + (row + 1);
  }

  // --- Restore Alt+Click cell reference insertion useEffect ---
  React.useEffect(() => {
    const hot = hotRef.current?.hotInstance;
    if (!hot) return;
    const root = hot.rootElement;
    if (!root) return;

    function handleMouseDown(e: MouseEvent) {
      if (!isEditingFormulaBar || !e.altKey || !formulaBarInputRef.current) return;
      let target = e.target as HTMLElement | null;
      // Walk up to the <td>
      while (target && target.nodeName !== 'TD') {
        target = target.parentElement;
      }
      if (!target) return;
      const hot = hotRef.current?.hotInstance;
      if (!hot) return;
      // Use Handsontable API to get cell coordinates
      const coords = hot.getCoords(target);
      if (!coords || coords.row < 0 || coords.col < 0) return;
      const row = coords.row;
      const col = coords.col;
      // Insert cell ref at cursor
      const input = formulaBarInputRef.current;
      const start = input.selectionStart ?? 0;
      const end = input.selectionEnd ?? 0;
      const cellRef = toExcelRef(row, col);
      const before = formulaBarValue.slice(0, start);
      const after = formulaBarValue.slice(end);
      const newValue = before + cellRef + after;
      setFormulaBarValue(newValue);
      formulaBarValueRef.current = newValue;
      setTimeout(() => {
        if (formulaBarInputRef.current) {
          const pos = start + cellRef.length;
          formulaBarInputRef.current.setSelectionRange(pos, pos);
          formulaBarInputRef.current.focus();
        }
      }, 0);
      e.preventDefault();
      e.stopPropagation();
    }
    root.addEventListener('mousedown', handleMouseDown, true);
    return () => {
      root.removeEventListener('mousedown', handleMouseDown, true);
    };
  }, [isEditingFormulaBar, formulaBarValue]);

  // Update formula bar when selection changes
  const handleAfterSelection = (row: number, col: number) => {
    selectedCellRef.current = [row, col];
    const hot = hotRef.current?.hotInstance;
    if (!hot) return;
    const formulasPlugin = hot.getPlugin('formulas');
    let value = '';
    formulaRefsRef.current = new Set();
    if (formulasPlugin && formulasPlugin.isFormulaCellType) {
      if (formulasPlugin.isFormulaCellType(row, col)) {
        // Get the formula string
        value = formulasPlugin.engine.getCellFormula({ sheet: formulasPlugin.sheetId, row, col }) ?? '';
        // Get referenced cells
        if (formulasPlugin.engine.getCellDependencies) {
          const deps = formulasPlugin.engine.getCellDependencies({ sheet: formulasPlugin.sheetId, row, col });
          if (Array.isArray(deps)) {
            for (const dep of deps) {
              if (dep.sheet === formulasPlugin.sheetId) {
                formulaRefsRef.current.add(`${dep.row},${dep.col}`);
              }
            }
          }
        }
      } else {
        value = hot.getDataAtCell(row, col) ?? '';
      }
    } else {
      value = hot.getDataAtCell(row, col) ?? '';
    }
    formulaBarValueRef.current = value;
    setFormulaBarValue(value); // Only update input, not the table
  };

  // Update cell value when formula bar is edited
  const handleFormulaBarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormulaBarValue(e.target.value);
    formulaBarValueRef.current = e.target.value;
  };

  const commitFormulaBarValue = () => {
    const selectedCell = selectedCellRef.current;
    if (!selectedCell) return;
    const [row, col] = selectedCell;
    const hot = hotRef.current?.hotInstance;
    if (!hot) return;
    hot.setDataAtCell(row, col, formulaBarValueRef.current);
    // Optionally, reselect the cell to update the formula bar
    setTimeout(() => {
      hot.selectCell(row, col);
    }, 0);
  };

  const handleFormulaBarKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      commitFormulaBarValue();
    }
  };

  // Switch sheets and persist data
  const handleSheetChange = (sheet: string) => {
    setSheetDataMapState(prev => ({ ...prev, [currentSheet]: data }));
    setCurrentSheet(sheet);
    setData(sheetDataMapState[sheet] || []);
    setMergeCells(sheetMergeCellsMap[sheet] || []);
  };

  // Export all sheets' data as JSON (with formulas)
  const handleExport = () => {
    const hot = hotRef.current?.hotInstance;
    if (!hot) return;
    const formulasPlugin = hot.getPlugin('formulas');
    const exportData: Record<string, any> = {};
    for (const sheetName of SHEETS) {
      const sheet = sheetDataMapState[sheetName];
      const rows = sheet.length;
      const cols = sheet[0]?.length || 0;
      const out: string[][] = [];
      for (let r = 0; r < rows; r++) {
        const row: string[] = [];
        for (let c = 0; c < cols; c++) {
          // Only get formula if this is the current sheet and formulasPlugin is available
          if (sheetName === currentSheet && formulasPlugin && formulasPlugin.isFormulaCellType && formulasPlugin.isFormulaCellType(r, c)) {
            const formula = formulasPlugin.engine.getCellFormula({ sheet: formulasPlugin.sheetId, row: r, col: c });
            row.push(formula ?? '');
          } else {
            row.push(sheet[r][c] ?? '');
          }
        }
        out.push(row);
      }
      // Query Handsontable for current colWidths for the current sheet
      let colWidths: number[] | undefined = undefined;
      if (userColWidths[sheetName]) {
        colWidths = userColWidths[sheetName];
      } else if (defaultSheets[sheetName]?.colWidths) {
        colWidths = defaultSheets[sheetName].colWidths;
      } else if (sheetName === 'Forecast Summary') {
        colWidths = Array.from({ length: cols }, (_, i) => forecastSummaryColWidths(i));
      } else if (sheetName === 'Total Summary') {
        colWidths = Array.from({ length: cols }, (_, i) => totalSummaryColWidths(i));
      } else if (sheetName === 'Sinking Fund Details') {
        colWidths = Array.from({ length: cols }, (_, i) => sinkingFundColWidths(i));
      } else if (sheetName === 'Report') {
        colWidths = Array.from({ length: cols }, (_, i) => reportColWidths(i));
      } else {
        colWidths = Array.from({ length: cols }, () => 100);
      }
      // In handleExport, use mergeCells state for export
      let mergeCellsExport: any[] | undefined = undefined;
      if (sheetName === currentSheet) {
        mergeCellsExport = mergeCells;
      } else if (sheetMergeCellsMap[sheetName]) {
        mergeCellsExport = sheetMergeCellsMap[sheetName];
      } else if (defaultSheets && defaultSheets[sheetName] && defaultSheets[sheetName].mergeCells) {
        mergeCellsExport = defaultSheets[sheetName].mergeCells;
      } else if (sheetName === 'Sinking Fund Details') {
        mergeCellsExport = sinkingFundMerges;
      } else if (sheetName === 'Total Summary') {
        mergeCellsExport = totalSummaryMerges;
      }
      exportData[sheetName] = {
        data: out,
        colWidths,
        mergeCells: mergeCellsExport,
      };
    }
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sheets-export.json';
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 0);
  };

  function colToIdx(col: string) {
    let result = 0;
    for (let i = 0; i < col.length; i++) {
        result *= 26;
        result += col.charCodeAt(i) - 'A'.charCodeAt(0) + 1;
    }
    return result - 1;
  }

  function idxToCol(idx: number) {
    let col = '';
    let n = idx;
    do {
        col = String.fromCharCode(65 + (n % 26)) + col;
        n = Math.floor(n / 26) - 1;
    } while (n >= 0);
    return col;
  }

  function adjustFormula(formula: string, srcRow: number, srcCol: number, targetRow: number, targetCol: number) {
    const rowDiff = targetRow - srcRow;
    const colDiff = targetCol - srcCol;

    if (rowDiff === 0 && colDiff === 0) {
        return formula;
    }

    const cellRefRegex = /([\$]?)([A-Z]+)([\$]?)(\d+)/g;

    return formula.replace(cellRefRegex, (match, colAbs, col, rowAbs, row) => {
        let newRow = parseInt(row, 10);
        if (!rowAbs) { // if row is not absolute
            newRow += rowDiff;
        }
        
        let newCol = col;
        if (!colAbs) { // if col is not absolute
            const colIdx = colToIdx(col);
            const newColIdx = colIdx + colDiff;
            if (newColIdx >= 0) {
              newCol = idxToCol(newColIdx);
            }
        }
        
        return `${colAbs || ''}${newCol}${rowAbs || ''}${newRow}`;
    });
  }

  // Add 'Hide' label to Sinking Fund Details header
  if (sheetDataMapState['Sinking Fund Details'][0]) {
    sheetDataMapState['Sinking Fund Details'][0][4] = 'Hide'; // Column E
  }

  // Compute which rows to hide for Sinking Fund Details
  let hiddenRows: number[] = [];
  if (
    currentSheet === 'Sinking Fund Details' &&
    !showHidden &&
    sheetDataMapState['Sinking Fund Details'].length > 1
  ) {
    hiddenRows = sheetDataMapState['Sinking Fund Details']
      .map((row, idx) => (idx === 0 ? null : row[4] && row[4] !== '' ? idx : null)) // Check Column E
      .filter((idx) => idx !== null) as number[];
  }

  // Memoize HotTable to avoid unnecessary re-renders
  const MemoHotTable = React.useMemo(() => (
    <HotTable
      ref={hotRef}
      data={data}
      colHeaders={true}
      rowHeaders={true}
      width="100%"
      height="100%"
      licenseKey="non-commercial-and-evaluation"
      formulas={{
        engine: hfInstance,
        sheetName: currentSheet,
      }}
      hiddenRows={hiddenRows.length > 0 ? { rows: hiddenRows } : undefined}
      mergeCells={mergeCells}
      colWidths={
        (() => {
          const colCount = data[0]?.length || 45;
          // 1. Check localStorage
          const localKey = `colWidths_${currentSheet}`;
          const local = localStorage.getItem(localKey);
          if (local) {
            try {
              const arr = JSON.parse(local);
              if (Array.isArray(arr)) {
                return arr;
              }
            } catch {}
          }
          // 2. Check defaultSheets
          if (defaultSheets[currentSheet]?.colWidths) {
            return defaultSheets[currentSheet].colWidths;
          }
          // 3. Hardcoded (always generate array)
          let arr: number[] = [];
          if (currentSheet === 'Forecast Summary') arr = Array.from({ length: colCount }, (_, i) => forecastSummaryColWidths(i));
          else if (currentSheet === 'Total Summary') arr = Array.from({ length: colCount }, (_, i) => totalSummaryColWidths(i));
          else if (currentSheet === 'Sinking Fund Details') arr = Array.from({ length: colCount }, (_, i) => sinkingFundColWidths(i));
          else if (currentSheet === 'Report') arr = Array.from({ length: colCount }, (_, i) => reportColWidths(i));
          else arr = Array.from({ length: colCount }, () => 100);
          return arr;
        })()
      }
      cells={(row, col) => {
        let classNames: string[] = [];
        if (currentSheet === 'Forecast Summary') {
          classNames = getForecastSummaryCellClassNames(row, col);
        } else if (currentSheet === 'Total Summary') {
          classNames = getTotalSummaryCellClassNames(row, col);
          if (classNames.includes('numeric-3dp')) {
            return {
              className: classNames.join(' '),
              type: 'numeric',
              numericFormat: { pattern: '0.000' }
            };
          }
        } else if (currentSheet === 'Sinking Fund Details') {
          classNames = getSinkingFundCellClassNames(row, col);
          if (classNames.includes('numeric-3dp')) {
            return {
              className: classNames.join(' '),
              type: 'numeric',
              numericFormat: { pattern: '0.000' }
            };
          }
        } else if (currentSheet === 'Report') {
          classNames = getReportCellClassNames(row, col);
        }
        // Highlight referenced cells
        if (formulaRefsRef.current.has(`${row},${col}`)) {
          classNames.push('htFormulaRef');
        }
        if (classNames.length > 0) {
          return { className: classNames.join(' ') };
        }
        return {};
      }}
      beforeCopy={(data, coords) => {
        const hot = hotRef.current?.hotInstance;
        if (!hot) return;

        const selection = coords[0];
        const { startRow, startCol, endRow, endCol } = selection;

        const copiedData: (string | null)[][] = [];
        for (let r = 0; r <= endRow - startRow; r++) {
            const rowData: (string|null)[] = [];
            for (let c = 0; c <= endCol - startCol; c++) {
                const val = hot.getSourceDataAtCell(startRow + r, startCol + c);
                rowData.push(val);
                data[r][c] = val;
            }
            copiedData.push(rowData);
        }
        copySourceRef.current = { startRow, startCol, endRow, endCol, data: copiedData };
      }}
      beforeChange={(changes, source) => {
        if (source === 'CopyPaste.paste' && copySourceRef.current && changes) {
            const { startRow: srcStartRow, startCol: srcStartCol, data: copiedData } = copySourceRef.current;
            const copyRows = copiedData.length;
            const copyCols = copiedData[0].length;

            for (let i = 0; i < changes.length; i++) {
                const change = changes[i];
                if (!change) continue;

                const [targetRow, targetCol_prop, , ] = change;
                const targetCol = typeof targetCol_prop === 'number' ? targetCol_prop : hotRef.current?.hotInstance.propToCol(targetCol_prop);

                if(targetCol === undefined) continue;

                // Map the pasted cell back to the copied cell
                const pastedRowIndex = (targetRow - (changes[0]?.[0] ?? 0)) % copyRows;
                const pastedColIndex = (targetCol - (typeof changes[0]?.[1] === 'number' ? changes[0]?.[1] : 0)) % copyCols;

                const sourceValue = copiedData[pastedRowIndex]?.[pastedColIndex];
                
                if (typeof sourceValue === 'string' && sourceValue.startsWith('=')) {
                    const sourceRow = srcStartRow + pastedRowIndex;
                    const sourceCol = srcStartCol + pastedColIndex;
                    
                    const adjustedFormula = adjustFormula(sourceValue, sourceRow, sourceCol, targetRow, targetCol);
                    change[3] = adjustedFormula;
                } else {
                    change[3] = sourceValue;
                }
            }
        }
      }}
      afterChange={(changes, source) => {
        if (source === 'edit' || source === 'CopyPaste.paste') {
          const newData = [...hotRef.current?.hotInstance?.getData()];
          setData(newData);
          setSheetDataMapState(prev => ({ ...prev, [currentSheet]: newData }));
        }
      }}
      afterSelection={handleAfterSelection}
      contextMenu={{
        items: {
          'row_above': {},
          'row_below': {},
          'remove_row': {},
          '---------1': '---------',
          'copy': {},
          'cut': {},
          '---------2': '---------',
          'col_left': {},
          'col_right': {},
          'remove_col': {},
          '---------3': '---------',
          'undo': {},
          'redo': {},
          ...(currentSheet === 'Sinking Fund Details' ? {
            'hide_col_e': {
              name: 'Hide',
              disabled: function () {
                const hot = hotRef.current?.hotInstance;
                const sel = hot?.getSelectedLast();
                if (!sel) return true;
                const [r1, c1, r2, c2] = sel;
                // Only enable if selection is entirely in col 4 (E)
                return !(c1 === 4 && c2 === 4);
              },
              hidden: function () {
                const hot = hotRef.current?.hotInstance;
                const sel = hot?.getSelectedLast();
                if (!sel) return true;
                const [r1, c1, r2, c2] = sel;
                // Only show if selection is entirely in col 4 (E)
                return !(c1 === 4 && c2 === 4);
              },
              callback: function () {
                const hot = hotRef.current?.hotInstance;
                const sel = hot?.getSelectedLast();
                if (!sel) return;
                const [r1, c1, r2, c2] = sel;
                if (!(c1 === 4 && c2 === 4)) return;
                const minRow = Math.min(r1, r2);
                const maxRow = Math.max(r1, r2);
                for (let row = minRow; row <= maxRow; row++) {
                  hot.setDataAtCell(row, 4, 'x');
                }
              }
            }
          } : {}),
          'mergeCellsCustom': {
            name: 'Merge cells',
            disabled: function () {
              const hot = hotRef.current?.hotInstance;
              const sel = hot?.getSelectedLast();
              if (!sel) return true;
              const [r1, c1, r2, c2] = sel;
              return r1 === r2 && c1 === c2;
            },
            callback: function () {
              const hot = hotRef.current?.hotInstance;
              const sel = hot?.getSelectedLast();
              if (!sel) return;
              const [startRow, startCol, endRow, endCol] = sel;
              handleMergeCells([
                ...mergeCells,
                {
                  row: Math.min(startRow, endRow),
                  col: Math.min(startCol, endCol),
                  rowspan: Math.abs(endRow - startRow) + 1,
                  colspan: Math.abs(endCol - startCol) + 1,
                }
              ]);
            },
            hidden: function () {
              const hot = hotRef.current?.hotInstance;
              const sel = hot?.getSelectedLast();
              if (!sel) return true;
              const [r1, c1, r2, c2] = sel;
              return r1 === r2 && c1 === c2;
            },
          },
          'unmergeCellsCustom': {
            name: 'Unmerge cells',
            disabled: function () {
              const hot = hotRef.current?.hotInstance;
              const sel = hot?.getSelectedLast();
              if (!sel) return true;
              const [r1, c1, r2, c2] = sel;
              // Only enable if at least one merged cell is fully inside the selection
              return !mergeCells.some((m: any) => (
                m.row >= Math.min(r1, r2) &&
                m.row + m.rowspan - 1 <= Math.max(r1, r2) &&
                m.col >= Math.min(c1, c2) &&
                m.col + m.colspan - 1 <= Math.max(c1, c2)
              ));
            },
            callback: function () {
              const hot = hotRef.current?.hotInstance;
              const sel = hot?.getSelectedLast();
              if (!sel) return;
              const [r1, c1, r2, c2] = sel;
              handleMergeCells(
                mergeCells.filter((m: any) =>
                  !(
                    m.row >= Math.min(r1, r2) &&
                    m.row + m.rowspan - 1 <= Math.max(r1, r2) &&
                    m.col >= Math.min(c1, c2) &&
                    m.col + m.colspan - 1 <= Math.max(c1, c2)
                  )
                )
              );
            },
            hidden: function () {
              const hot = hotRef.current?.hotInstance;
              const sel = hot?.getSelectedLast();
              if (!sel) return true;
              const [r1, c1, r2, c2] = sel;
              // Hide if no merged cell is fully inside the selection
              return !mergeCells.some((m: any) => (
                m.row >= Math.min(r1, r2) &&
                m.row + m.rowspan - 1 <= Math.max(r1, r2) &&
                m.col >= Math.min(c1, c2) &&
                m.col + m.colspan - 1 <= Math.max(c1, c2)
              ));
            },
          },
        }
      }}
      autoWrapRow={false}
      autoWrapCol={false}
      manualColumnResize={true}
      outsideClickDeselects={false}
      afterBeginEditing={(row, col) => {
        setIsEditingCell(true);
        const hot = hotRef.current?.hotInstance;
        if (hot) {
          const editor = hot.getActiveEditor();
          cellEditorRef.current = editor;
        }
      }}
      afterColumnResize={(newSize, col, isDoubleClick) => {
        setUserColWidths(prev => {
          const colCount = data[0]?.length || 45;
          // Always start from the latest array used in the UI
          let baseWidths: number[] = [];
          const localKey = `colWidths_${currentSheet}`;
          const local = localStorage.getItem(localKey);
          if (local) {
            try {
              const arr = JSON.parse(local);
              if (Array.isArray(arr)) baseWidths = arr;
            } catch {}
          }
          if (!baseWidths.length && defaultSheets[currentSheet]?.colWidths) {
            baseWidths = defaultSheets[currentSheet].colWidths;
          }
          if (!baseWidths.length) {
            if (currentSheet === 'Forecast Summary') baseWidths = Array.from({ length: colCount }, (_, i) => forecastSummaryColWidths(i));
            else if (currentSheet === 'Total Summary') baseWidths = Array.from({ length: colCount }, (_, i) => totalSummaryColWidths(i));
            else if (currentSheet === 'Sinking Fund Details') baseWidths = Array.from({ length: colCount }, (_, i) => sinkingFundColWidths(i));
            else if (currentSheet === 'Report') baseWidths = Array.from({ length: colCount }, (_, i) => reportColWidths(i));
            else baseWidths = Array.from({ length: colCount }, () => 100);
          }
          const newWidths = [...baseWidths];
          newWidths[col] = newSize;
          for (let i = 0; i < colCount; i++) {
            if (typeof newWidths[i] !== 'number') newWidths[i] = baseWidths[i];
          }
          try {
            localStorage.setItem(`colWidths_${currentSheet}`, JSON.stringify(newWidths));
          } catch {}
          return { ...prev, [currentSheet]: newWidths };
        });
      }}
    />
  ), [data, currentSheet, showHidden, mergeCells]);

  // On merge/unmerge, update only the current sheet's merges
  const handleMergeCells = (newMerges: any[]) => {
    setMergeCells(newMerges);
  };

  // --- Alt+Click cell reference insertion for inline cell editor ---
  React.useEffect(() => {
    if (!isEditingCell || !cellEditorRef.current) return;
    const hot = hotRef.current?.hotInstance;
    if (!hot) return;
    const root = hot.rootElement;
    if (!root) return;
    // Get the textarea for the inline editor
    const editor = cellEditorRef.current;
    // Handsontable TextEditor uses .TEXTAREA
    const textarea = editor.TEXTAREA || (editor.TEXTAREA_PARENT && editor.TEXTAREA_PARENT.querySelector('textarea'));
    if (!textarea) return;

    function handleInlineAltClick(e: MouseEvent) {
      if (!e.altKey) return;
      let target = e.target as HTMLElement | null;
      // Walk up to the <td>
      while (target && target.nodeName !== 'TD') {
        target = target.parentElement;
      }
      if (!target) return;
      const coords = hot.getCoords(target);
      if (!coords || coords.row < 0 || coords.col < 0) return;
      const row = coords.row;
      const col = coords.col;
      // Insert cell ref at cursor in the textarea
      const input = textarea;
      const start = input.selectionStart ?? 0;
      const end = input.selectionEnd ?? 0;
      const cellRef = toExcelRef(row, col);
      const before = input.value.slice(0, start);
      const after = input.value.slice(end);
      const newValue = before + cellRef + after;
      input.value = newValue;
      // Set cursor after inserted cell ref
      setTimeout(() => {
        input.setSelectionRange(start + cellRef.length, start + cellRef.length);
        input.focus();
      }, 0);
      e.preventDefault();
      e.stopPropagation();
    }
    root.addEventListener('mousedown', handleInlineAltClick, true);
    return () => {
      root.removeEventListener('mousedown', handleInlineAltClick, true);
    };
  }, [isEditingCell, cellEditorRef.current]);

  // Clear colWidths from localStorage on page reload
  React.useEffect(() => {
    window.addEventListener('beforeunload', () => {
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('colWidths_')) localStorage.removeItem(key);
      });
    });
  }, []);

  return (
    <div style={{ height: '100vh', width: '100vw', display: 'flex', flexDirection: 'column', margin: 0, padding: 0, position: 'fixed', top: 0, left: 0 }}>
      {/* Page content */}
      <div style={{ flex: 1, minHeight: 0, minWidth: 0, height: '100%', width: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Formula bar: show only on spreadsheet pages */}
        {page !== 'Report' && (
          <div style={{ display: 'flex', alignItems: 'center', padding: '8px 16px', background: '#f9f9f9', borderBottom: '1px solid #e0e0e0' }}>
            <span style={{ fontWeight: 'bold', color: '#1976d2', marginRight: 8 }}>fx</span>
            <input
              ref={formulaBarInputRef}
              type="text"
              value={formulaBarValue}
              onChange={handleFormulaBarChange}
              onKeyDown={handleFormulaBarKeyDown}
              onFocus={() => setIsEditingFormulaBar(true)}
              onBlur={() => setIsEditingFormulaBar(false)}
              style={{ flex: 1, fontSize: 16, padding: '6px 10px', border: '1px solid #bdbdbd', borderRadius: 4, background: '#fff' }}
              spellCheck={false}
              autoComplete="off"
            />
          </div>
        )}
        {/* Main content */}
        {page === 'Report' && <Report />}
        {page === 'Forecast Summary' && MemoHotTable}
        {page === 'Total Summary' && MemoHotTable}
        {page === 'Sinking Fund Details' && MemoHotTable}
      </div>
      {/* Navigation bar at the bottom */}
      <div style={{ padding: 8, background: '#f0f0f0', textAlign: 'center', position: 'relative' }}>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8 }}>
          <button
            onClick={() => setPage('Report')}
            style={{
              margin: '0 8px',
              padding: '8px 16px',
              background: page === 'Report' ? '#1976d2' : '#fff',
              color: page === 'Report' ? '#fff' : '#1976d2',
              border: '1px solid #1976d2',
              borderRadius: 4,
              cursor: 'pointer',
            }}
          >
            Report
          </button>
          <button
            onClick={() => { setPage('Forecast Summary'); handleSheetChange('Forecast Summary'); }}
            style={{
              margin: '0 8px',
              padding: '8px 16px',
              background: page === 'Forecast Summary' ? '#1976d2' : '#fff',
              color: page === 'Forecast Summary' ? '#fff' : '#1976d2',
              border: '1px solid #1976d2',
              borderRadius: 4,
              cursor: 'pointer',
            }}
          >
            Forecast Summary
          </button>
          <button
            onClick={() => { setPage('Total Summary'); handleSheetChange('Total Summary'); }}
            style={{
              margin: '0 8px',
              padding: '8px 16px',
              background: page === 'Total Summary' ? '#1976d2' : '#fff',
              color: page === 'Total Summary' ? '#fff' : '#1976d2',
              border: '1px solid #1976d2',
              borderRadius: 4,
              cursor: 'pointer',
            }}
          >
            Total Summary
          </button>
          <button
            onClick={() => { setPage('Sinking Fund Details'); handleSheetChange('Sinking Fund Details'); }}
            style={{
              margin: '0 8px',
              padding: '8px 16px',
              background: page === 'Sinking Fund Details' ? '#1976d2' : '#fff',
              color: page === 'Sinking Fund Details' ? '#fff' : '#1976d2',
              border: '1px solid #1976d2',
              borderRadius: 4,
              cursor: 'pointer',
            }}
          >
            Sinking Fund Details
          </button>
        </div>
        {/* Export button bottom left, only show on non-Report pages */}
        {page !== 'Report' && (
          <div style={{ position: 'absolute', left: 8, bottom: 8, display: 'flex', alignItems: 'center', zIndex: 2 }}>
            <button
              onClick={handleExport}
              style={{
                padding: '8px 16px',
                background: '#1976d2',
                color: '#fff',
                border: '1px solid #1976d2',
                borderRadius: 4,
                cursor: 'pointer',
                marginRight: 8,
              }}
            >
              Export
            </button>
            {/* Show Hidden checkbox */}
            {page === 'Sinking Fund Details' && (
              <label style={{ display: 'flex', alignItems: 'center', fontSize: 14, background: '#fff', borderRadius: 4, padding: '4px 8px', border: '1px solid #1976d2', color: '#1976d2', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={showHidden}
                  onChange={e => setShowHidden(e.target.checked)}
                  style={{ marginRight: 4 }}
                />
                Show Hidden
              </label>
            )}
          </div>
        )}
      </div>
    </div>
  );
} 